import React, { useEffect, useMemo, useState } from 'react';
import { formatDate } from '@common';
import { Combobox, InputBase, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useVersionSelectContext } from '../../context';
import { VersionSelectOption } from './components';
import { getFilteredOptions } from './helpers';

type VersionSelectComboboxPropsType = {
  versions: string[];
  routePath: string;
  urlParams: Record<string, string>;
};

export const VersionSelectCombobox: React.FC<
  VersionSelectComboboxPropsType
> = ({ versions, routePath, urlParams }) => {
  const { t } = useTranslation();
  const { selectedVersion } = useVersionSelectContext();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const formattedSelectedVersion = useMemo(
    () => (selectedVersion ? formatDate(selectedVersion) : ''),
    [selectedVersion]
  );
  const [search, setSearch] = useState(formattedSelectedVersion);

  useEffect(() => {
    setSearch(formattedSelectedVersion);
  }, [formattedSelectedVersion]);

  const filteredOptions = getFilteredOptions({ versions, search });

  const handleOptionClick = (version: string) => {
    combobox.closeDropdown();
    setSearch(formatDate(version));
  };

  const options = filteredOptions.map((version) => (
    <VersionSelectOption
      value={version}
      key={version}
      to={routePath}
      params={{ ...urlParams, version }}
      onClick={() => handleOptionClick(version)}
    >
      {formatDate(version)}
    </VersionSelectOption>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={() => {
        combobox.closeDropdown();
      }}
      onOpen={() => {
        setSearch('');
      }}
    >
      <Combobox.Target>
        <InputBase
          rightSection={<Combobox.Chevron />}
          value={search}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch(formattedSelectedVersion);
          }}
          miw="200px"
          placeholder={formattedSelectedVersion}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>{t('NOTHING_FOUND')}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
